import { inject, Injectable } from '@angular/core';
import { injectMutation, injectQuery, QueryClient } from '@tanstack/angular-query-experimental';
import { lastValueFrom } from 'rxjs';

import { QUERY_KEYS } from '@core/constans';
import { ApiResponse } from '@core/types';
import { PaginationConfig, PaginationResult } from '@core/services/unit.service';
import { Assembly, AssemblyCreate, AssemblyDetail, AssemblyUpdate } from '@core/types/assembly.type';
import { AssemblyService } from '@core/services';
import { environment } from '@environments/environment';

@Injectable({ providedIn: 'root' })
export class AssemblyQuery {
  private readonly service = inject(AssemblyService);
  private readonly queryClient = inject(QueryClient);

  useAssembliesGrid(config: () => PaginationConfig) {
    return injectQuery(() => {
      const c = config();
      return {
        queryKey: QUERY_KEYS.assemblies.grid(c),
        queryFn: () => lastValueFrom(this.service.grid(c)),
        select: (res: ApiResponse<PaginationResult<Assembly>>) => res.data,
        staleTime: environment.staleTime,
      };
    });
  }

  useAssemblyDetail(id: () => string | null) {
    return injectQuery(() => {
      const v = id();
      return {
        queryKey: QUERY_KEYS.assemblies.detail(v || ''),
        queryFn: () => lastValueFrom(this.service.get(v!)),
        select: (res: ApiResponse<AssemblyDetail>) => res.data,
        enabled: !!v,
      };
    });
  }

  useCreateAssembly() {
    return injectMutation(() => ({
      mutationFn: (data: AssemblyCreate) => lastValueFrom(this.service.create(data)),
      onSuccess: () => this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.assemblies.all }),
    }));
  }

  useUpdateAssembly() {
    return injectMutation(() => ({
      mutationFn: (data: AssemblyUpdate) => lastValueFrom(this.service.update(data)),
      onSuccess: (_res, variables) => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.assemblies.detail(variables.id) });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.assemblies.all });
      },
    }));
  }

  useDeleteAssembly() {
    return injectMutation(() => ({
      mutationFn: (id: string) => lastValueFrom(this.service.delete(id)),
      onSuccess: () => this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.assemblies.all }),
    }));
  }
}

